import { useState } from "react";

import { ChevronDown, PlusIcon } from "lucide-react";
import { toast } from "sonner";

import { isWorkspaceAdmin } from "@/components/helpers/workspace/permissions";
import { useAuth } from "@/components/lib/auth";
import { CreateWorkspaceDialog } from "@/components/pages_logged_in/workspace/nav/SideNav/CreateWorkspaceDialog";
import { WorkspaceMenuLogo } from "@/components/pages_logged_in/workspace/nav/SideNav/WorkspaceMenuLogo";
import {
  getJwtToken,
  getRefreshToken,
  hasValidToken,
  setTokens,
} from "@/components/session/JwtTokenManager";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useParticipatedWorkspacesQuery } from "@/graphql";
import { cn } from "@/lib/utils";
import { DEFAULT_PERSONAL_WOKSPACE } from "@/types/workspaces";
import { isSingleAccount } from "@/utils/workspaces";

export function WorkspaceSwitching({ pageLoading = false }) {
  const [createWorkspaceVisible, setCreateWorkspaceVisible] = useState(false);
  const { currentWorkspace, currentUser } = useAuth();

  const { data, loading } = useParticipatedWorkspacesQuery({
    skip: !hasValidToken(),
  });

  const showLoader = pageLoading || loading;
  const workspaces = data?.participatedWorkspaces ?? [];
  const hasIndividual = currentUser.hasIndividualAccount;
  const selectedWorkspace = workspaces.find(
    (w) => w.urlSlug === currentWorkspace.urlSlug,
  );

  const handleWorkspaceSelect = (urlSlug: string) => {
    if (urlSlug === currentWorkspace.urlSlug) {
      return;
    }

    if (USE_SUBDOMAINS) {
      window.location.href = `/to-workspace/${urlSlug || "personal-mode"}`;
    } else {
      setTokens({
        token: getJwtToken(),
        refreshToken: getRefreshToken(),
        workspaceSlug: urlSlug,
        success: () => {
          window.location.href = "/dashboard";
        },
        error: () => {
          toast.error("Failed to switch workspace");
        },
      });
    }
  };

  // If the workspaces call is still loading, we should show a skeleton
  if (showLoader) {
    return (
      <div className="border-b h-[60px] py-2 px-3 flex items-center">
        <div className="flex items-center justify-between p-2 w-full">
          <div className="flex items-center">
            <Skeleton className="h-6 w-6 rounded-full" />
            <Skeleton className="h-4 w-20 ml-2.5" />
          </div>
          <Skeleton className="h-4 w-4" />
        </div>
      </div>
    );
  }

  // Early return for single account
  if (isSingleAccount(workspaces, hasIndividual) && !IS_EXEC) {
    return <WorkspaceMenuLogo isLink={true} workspace={selectedWorkspace} />;
  }

  // Early return for exec single account when user is not workspace admin
  // and does not have an individual account. We should potentially flip the
  // single account flag to false for these users in the future.
  if (
    workspaces.length === 1 &&
    !hasIndividual &&
    IS_EXEC &&
    !isWorkspaceAdmin()
  ) {
    const workspace = workspaces[0];

    return (
      <div className="border-b h-[60px]">
        <div className="flex items-center justify-between p-5">
          <div className="flex items-center">
            <WorkspaceMenuLogo
              isLink={false}
              workspace={workspace}
              iconSize={24}
              fitSize
              margin={0}
            />
            <div className="mx-2.5 overflow-hidden text-ellipsis font-medium text-sm text-nowrap md:max-w-36">
              {workspace.name}
            </div>
          </div>
        </div>
      </div>
    );
  }

  let options = [...workspaces];
  if (hasIndividual) {
    options.push(DEFAULT_PERSONAL_WOKSPACE);
  }

  return (
    <div className="border-b h-[60px] py-2 px-3 flex align-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div className="flex items-center justify-between p-2 cursor-pointer rounded hover:text-blue-700 hover:bg-slate-50 w-full">
            <div className="flex items-center">
              <WorkspaceMenuLogo
                isLink={false}
                workspace={selectedWorkspace}
                iconSize={24}
                fitSize
                margin={0}
              />
              <div
                className={cn(
                  "mx-2.5 overflow-hidden text-ellipsis font-medium text-sm text-nowrap md:max-w-28",
                  showLoader && "opacity-50",
                )}
              >
                {selectedWorkspace
                  ? selectedWorkspace.name
                  : DEFAULT_PERSONAL_WOKSPACE.name}
              </div>
            </div>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent sideOffset={-4} align="start">
          {options.map((workspace) => (
            <DropdownMenuItem
              key={workspace.id}
              className={cn(
                "cursor-pointer p-4 min-w-md md:min-w-64",
                currentWorkspace.id === workspace.id &&
                  "bg-gray-200 text-primary font-semibold",
              )}
              onClick={() => handleWorkspaceSelect(workspace.urlSlug)}
            >
              <div className="flex items-center w-full">
                <div
                  className={cn(
                    currentWorkspace.id !== workspace.id &&
                      "grayscale opacity-70",
                  )}
                >
                  <WorkspaceMenuLogo
                    workspace={
                      workspace == DEFAULT_PERSONAL_WOKSPACE
                        ? undefined
                        : workspace
                    }
                    isLink={false}
                    iconSize={18}
                    fitSize
                    margin={0}
                  />
                </div>
                <div className="flex-1 mx-2.5">{workspace.name}</div>
              </div>
            </DropdownMenuItem>
          ))}
          {(isWorkspaceAdmin() || currentWorkspace.isPersonal) && (
            <DropdownMenuItem
              key={"create-workspace-button"}
              onClick={() => setCreateWorkspaceVisible(true)}
            >
              <div className="flex items-center h-8">
                <PlusIcon className="text-muted-foreground mr-2" />
                <div>Create New Workspace</div>
              </div>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateWorkspaceDialog
        visible={createWorkspaceVisible}
        setVisible={setCreateWorkspaceVisible}
      />
    </div>
  );
}
